import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Route } from 'type-route';
import { z } from 'zod';

import { projectResourceName } from '../../helpers/util';
import { createServiceAccount } from '../../hooks/use_iam_api';
import { routes } from '../../router';
import { Breadcrumbs } from '../core/breadcrumbs';
import { createToast } from '../core/toast';

interface ServiceAccountCreateProps {
  route: Route<typeof routes.serviceAccountCreate>;
}

interface ServiceAccountCreateFormData {
  name: string;
  description: string;
  jwks: string;
}

const letterStartRegExp = new RegExp('^[a-z]');
const letterNumberEndRegExp = new RegExp('[a-z0-9]$');
const nameRegExp = new RegExp('^[-a-z0-9]*$');

export function ServiceAccountCreate(props: ServiceAccountCreateProps) {
  const { route } = props;
  const schema = z.object({
    name: z
      .string()
      .min(1, 'Name is required')
      .max(63, 'Name may be at most 63 characters')
      .regex(nameRegExp, {
        message:
          'Name must consist of lowercase letters (a-z), numbers, and hyphens',
      })
      .regex(letterStartRegExp, {
        message: 'Name must start with a lowercase letter',
      })
      .regex(letterNumberEndRegExp, {
        message: 'Name must end with a lowercase letter or a number',
      }),
    description: z.string(),
    jwks: z.string().min(1, 'JWKS is required'),
  });
  const { register, handleSubmit, formState } = useForm<
    ServiceAccountCreateFormData
  >({
    resolver: zodResolver(schema),
  });

  const parent = projectResourceName(route.params.projectId);

  const { isSubmitting, errors } = formState;

  const onSubmit = async (data: ServiceAccountCreateFormData) => {
    try {
      await createServiceAccount({
        parent: parent,
        serviceAccount: {
          name: '',
          state: 0,
          description: data.description,
          jwks: data.jwks,
        },
        serviceAccountId: data.name,
      });
      createToast(`Service account "${data.name}" created.`);
      routes.serviceAccountDetail({
        projectId: route.params.projectId,
        serviceAccountId: data.name,
      }).push();
    } catch (err) {
      createToast(`Error creating service account: ${err}`);
    }
  };

  const breadcrumbs = [
    {
      name: 'Service accounts',
      link: routes.serviceAccountList({
        projectId: route.params.projectId,
      }).link,
      current: true,
    },
  ];

  return (
    <div>
      <Breadcrumbs pages={breadcrumbs} />
      <div className="mt-2 md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-medium text-gray-900">
            New service account
          </h1>
        </div>
      </div>
      <p className="mt-2 max-w-xl text-sm text-gray-500">
        A service account is a special kind of account typically used by an
        application, rather than a person.
      </p>
      <form
        className="max-w-xl space-y-8 divide-y divide-gray-200"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="mt-6 space-y-8">
          <div className="space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Service account name
              </label>
              <div
                className={`mt-1 ${
                  errors.name ? 'relative rounded-md shadow-sm' : ''
                }`}
              >
                <input
                  {...register('name')}
                  id="name"
                  type="text"
                  className={`block w-full rounded-md shadow-sm sm:text-sm ${
                    errors.name
                      ? 'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                      : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                  }`}
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm text-red-600" id="name-error">
                {errors.name?.message}
              </p>
            </div>
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <div
                className={`mt-1 ${
                  errors.description ? 'relative rounded-md shadow-sm' : ''
                }`}
              >
                <input
                  {...register('description')}
                  id="description"
                  type="text"
                  className={`block w-full rounded-md shadow-sm sm:text-sm ${
                    errors.description
                      ? 'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                      : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                  }`}
                  aria-invalid={!!errors.description}
                />
                {errors.description && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm text-red-600" id="description-error">
                {errors.description?.message}
              </p>
            </div>
            <div>
              <label
                htmlFor="jwks"
                className="block text-sm font-medium text-gray-700"
              >
                JSON Web Key Set (JWKS)
              </label>
              <div
                className={`mt-1 ${
                  errors.jwks ? 'relative rounded-md shadow-sm' : ''
                }`}
              >
                <textarea
                  rows={6}
                  {...register('jwks')}
                  id="jwks"
                  className={`block w-full rounded-md shadow-sm sm:text-sm ${
                    errors.jwks
                      ? 'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                      : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                  }`}
                  defaultValue={''}
                  aria-invalid={!!errors.jwks}
                />
                {errors.jwks && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm text-red-600" id="jwks-error">
                {errors.jwks?.message}
              </p>
            </div>
          </div>
        </div>
        <div className="pt-5">
          <div className="flex justify-end">
            <a
              {...routes.serviceAccountList({
                projectId: route.params.projectId,
              }).link}
              role="button"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              className={`ml-3 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                isSubmitting
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:bg-indigo-700'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
